import { createContext, useContext, useReducer, useRef, useState, type ReactNode } from 'react'

import { heldEra, inHeldEra, useSessionEra } from '../documents/held'

/**
 * Every action that has been dispatched and not settled, for the whole project.
 *
 * An unsettled action is the one piece of state on this application that must
 * outlive the screen that started it, and it used to be the piece most easily
 * lost. It lived in the component: a `useState` on the record screen, or on one
 * of its panels. Two ordinary movements threw it away or mis-aimed it.
 *
 * **The same screen, a different record.** Changing only the `:recordId`
 * segment is the same route, so the router keeps the element mounted and every
 * `useState` in it survives -- now beside another record. The held attempt was
 * still there and its send function took the destination from the *current*
 * route, so a retry of the first record's action dispatched against the second.
 * The same shape as the project-segment defect the shell keys against, one
 * level down, and landing on the one kind of state that must never be wrong.
 *
 * **Leaving the screen at all.** The record screen returns a spinner while an
 * uncached record loads, which unmounts the panels below it; the register link
 * unmounts the screen outright. Either one dropped a transfer that may be on
 * the instance, or a response that may be on the step, with nothing on screen
 * to say it had gone.
 *
 * So the holding is here, above the outlet, and each entry carries the record
 * it was raised against. A screen showing record A sees A's entries and no
 * others. An entry for a record that is not on screen is not lost and not
 * silently held either: the shell lists it with the way back to it.
 *
 * Scoped to the project, because the provider is mounted inside the shell's
 * project key: changing project unmounts it, which is right -- an action on
 * another job is not something to offer a retry for from here.
 *
 * **Nothing is persisted.** No storage, no second tab, no reload. A dispatched
 * action whose answer was lost is a fact about this browsing session, and a
 * copy of it in storage would be a second claim about the record that nothing
 * keeps true.
 *
 * **Nor does it outlive the session that sent it.** The provider stays mounted
 * when one person signs out and the next signs in at the same browser, so each
 * entry carries the session it was dispatched in, and only the current
 * session's entries are offered -- to a screen, to the shell's list, and to a
 * retry. A screen that filtered by session itself was not enough: the shell
 * listed the last person's held creation, with a Retry that sent it again.
 */

export type UnsettledEntry = {
  /**
   * `${prefix}::${on}` -- one unresolved action of a kind per record.
   *
   * A second press of the same action on the same record while the first is
   * unsettled is not a thing this application offers: the fields stay shut
   * until the question is answered.
   */
  id: string
  /** The record it was raised against, frozen when it was dispatched. */
  on: string
  /** Which action, e.g. `submittal-revision`. Also the key's prefix. */
  prefix: string
  /** The idempotency key it was sent under, held so a retry is a replay. */
  key: string
  /** Where the reader gets back to it. */
  where: string
  /** What to call that record in a list -- its number, not its identifier. */
  label: string
  /**
   * Whether re-sending it away from that record's own screen means the same
   * thing.
   *
   * True for a form submission: the payload, the key and the record are all
   * frozen, so the replay is the same request wherever it is pressed. False
   * for a file transfer, where the way out depends on what became of it -- a
   * transfer the instance says is not there has to be opened again under a new
   * key, and only the panel that owns the file knows which of the two this is.
   * Those are linked back to rather than retried from a distance.
   */
  retryHere?: boolean
  /**
   * Set while this reader's read of the record is refused.
   *
   * The attempt is still held and still this session's, but the shell's list
   * then names neither the record nor the way back to it, and offers no
   * replay from a distance: what it was raised against is no longer this
   * reader's to see. Only the record's own screen sets and clears it, from
   * what its reads have been answered.
   */
  withheld?: boolean
  /** The session it was dispatched in, which is the only one it is offered to. */
  since: number
  status: 'pending' | 'unsettled'
  error: unknown
  /**
   * Whatever the screen that raised it needs in order to draw it and resume it.
   *
   * Opaque here on purpose. A file transfer is not a form submission: it has a
   * file, a caption and five possible outcomes rather than two, and teaching
   * this store about any of that would make it the second place that knows how
   * uploads work. What it does know is that the thing must outlive the screen.
   */
  detail?: unknown
}

/** Everything about an entry that the caller supplies when it dispatches. */
export type Held = Omit<UnsettledEntry, 'status' | 'error' | 'since'>

/**
 * How an entry is re-sent.
 *
 * Frozen when the action is dispatched and called with `false` for every later
 * attempt, so a retry carries the payload and key of the attempt it is a retry
 * of and goes to the record that attempt was raised against -- whatever is on
 * screen by then, and whether or not the screen that started it is still
 * mounted.
 */
type Run = (first: boolean) => Promise<void>

export type UnsettledWork = {
  entries: UnsettledEntry[]
  /**
   * Every entry the store holds, of any session.
   *
   * For the store's own specification, which has to see what `entries`
   * filters away in order to assert that nothing is left behind. Screens read
   * `entries`.
   */
  everyEntry: UnsettledEntry[]
  /** Record an attempt and dispatch it. */
  hold: (held: Held, run: Run) => void
  /** Re-send a held attempt, unchanged. */
  again: (id: string) => void
  /** Its outcome is known: forget it. */
  release: (id: string) => void
  /**
   * Forget the entry an earlier session dispatched, and only that one.
   *
   * By id alone, as {@link release} does, it would take whatever holds the id
   * now -- and the next session may have raised the same action on the same
   * record since. So it removes the entry only if it is still the one from
   * `since`, and the run only if it is still `run`.
   */
  releaseStale: (id: string, since: number, run: Run) => void
  /** Its answer did not come back. */
  mark: (id: string, error: unknown) => void
  /** Change what is held about it, without changing whether it is held. */
  update: (id: string, change: Partial<UnsettledEntry>) => void
  /**
   * This reader's read of one record has ended, and what established it.
   *
   * Kept here rather than on the screen that learned it, for the same reason
   * the attempts are: it is learned by an action that may settle after the
   * screen has gone. A hand-off replayed from the register answers into a
   * component that unmounted two navigations ago, and a `useState` there
   * receives the receipt and drops it -- so the next screen for that record
   * offered controls for an authorisation that had already gone, and the
   * receipt itself, the only account the person has of where the record went,
   * was never shown at all.
   *
   * Set once per record and never unset: nothing that happens afterwards gives
   * the read back.
   */
  endAccess: (on: string, receipt: unknown) => void
  /**
   * What ended the read of that record, asked **now**.
   *
   * Reads through a ref rather than a captured value, because its callers are
   * frozen closures: a transfer already running asks this between calls that
   * can be minutes apart, and the answer it needs is the one that is true when
   * it asks. A panel that consulted its own component-scoped copy went on
   * sending parts of a file for a record whose read had ended, because the
   * receipt had reached a *later* panel and the running transfer still held
   * the ref of the one that dispatched it.
   */
  accessEndedOn: (on: string) => unknown
}

const Work = createContext<UnsettledWork | null>(null)

export function UnsettledWorkProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<UnsettledEntry[]>([])
  // Out of render state on purpose: a closure is not a value to compare
  // renders by, and it must survive the unmount of whatever created it.
  const runs = useRef(new Map<string, Run>())
  // The same, and for the same reason: what is read here is read from inside
  // those closures, so it has to be the value now rather than the value at the
  // render that captured the map. The redraw is what makes a screen notice.
  const ended = useRef(new Map<string, unknown>())
  // A render is what carries it: the map itself is a ref, so nothing below
  // notices it change until something asks React to draw again.
  const [, redraw] = useReducer((n: number) => n + 1, 0)
  // Drawn again when the session ends, so what the last one held goes from
  // every screen at once rather than at whichever render comes next.
  useSessionEra()

  /**
   * Built each render rather than memoised.
   *
   * A memo here has to list everything the holding can change by -- the
   * entries, and a counter for the access map, which lives in a ref so that
   * frozen callers read it live. A dependency that exists only to say "and
   * also this changed" is a dependency the linter is right to call
   * unnecessary and a reader is right to distrust: forget it and a receipt
   * stops reaching the screens, which is silent and is exactly the class of
   * defect this file is about. Rebuilding the value each render costs the
   * screens below a render they were about to do anyway: this provider
   * re-renders when the holding changes, and otherwise only when the shell
   * above it does, which is a navigation.
   */
  const value = ((): UnsettledWork => {
    const patch = (id: string, change: Partial<UnsettledEntry>) =>
      setEntries((held) => held.map((entry) => (entry.id === id ? { ...entry, ...change } : entry)))

    const release = (id: string) => {
      runs.current.delete(id)
      setEntries((held) => held.filter((entry) => entry.id !== id))
    }

    return {
      entries: entries.filter((entry) => inHeldEra(entry.since)),
      everyEntry: entries,
      hold: (held, run) => {
        runs.current.set(held.id, run)
        setEntries((was) => [
          ...was.filter((entry) => entry.id !== held.id),
          { ...held, since: heldEra(), status: 'pending', error: null },
        ])
        void run(true)
      },
      again: (id) => {
        const run = runs.current.get(id)
        // Only the session that sent it may send it again: an entry from an
        // earlier one is not offered, and is not dispatched if asked for.
        const entry = entries.find((held) => held.id === id)
        if (!run || !entry || !inHeldEra(entry.since)) return
        patch(id, { status: 'pending', error: null })
        void run(false)
      },
      release,
      releaseStale: (id, since, run) => {
        if (runs.current.get(id) === run) runs.current.delete(id)
        setEntries((held) => held.filter((entry) => entry.id !== id || entry.since !== since))
      },
      mark: (id, error) => patch(id, { status: 'unsettled', error }),
      update: patch,
      endAccess: (on, receipt) => {
        if (ended.current.has(on)) return
        ended.current.set(on, receipt)
        redraw()
      },
      accessEndedOn: (on) => ended.current.get(on),
    }
  })()

  return <Work.Provider value={value}>{children}</Work.Provider>
}

/**
 * The holding, from a screen inside it.
 *
 * Throws rather than falling back to a local one. A screen holding an
 * unsettled action outside this provider is the defect this exists to repair,
 * and a silent fallback would make it look repaired.
 */
export function useUnsettledWork(): UnsettledWork {
  const work = useContext(Work)
  if (!work) throw new Error('unsettled work was asked for outside its provider')
  return work
}

/** The entries raised against one record, which is all a screen may act on. */
export function workOn(work: UnsettledWork, on: string): UnsettledEntry[] {
  return work.entries.filter((entry) => entry.on === on)
}

/**
 * The entries whose own screen is not this one, which the shell points at.
 *
 * By the way back rather than by the record, because not every held action is
 * raised against a record: a submittal being created is raised against the
 * project, from a screen with no record segment in its route at all.
 */
export function workElsewhere(work: UnsettledWork, screen: string): UnsettledEntry[] {
  return work.entries.filter((entry) => entry.where !== screen)
}
